/**
 * Construit un BulletinData complet depuis un PayrollResult + infos employee/company.
 * Structure calquee sur le modele LPG.
 */

import type { BulletinData, BulletinLine } from './bulletin-types'
import type { PayrollResult } from '../payroll-engine'
import {
  FFO_MENSUEL_CENTS, FDS_MENSUEL_CENTS, AC_MENSUEL_CENTS,
  COTISATIONS_PATRONALES, CLASSES_MUTUALITE,
  PLAFOND_COTISABLE_CENTS,
} from '../../../config/rates'

function lastDayOfMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate()
}

interface BuildInput {
  result: PayrollResult
  employee: {
    id: string
    firstName: string
    lastName: string
    taxClass: string
    rtsPersonalRate: number | null
    matriculeCcss: string | null
    department: string | null
    position: string | null
    grossMonthlySalary: number | null
    weeklyHours: number | null
  }
  company: { name: string }
  year: number
  month: number
  overtimeHours: number
  mealVouchersCount: number
  /** Bulletins precedents de l'annee (pour les cumuls) */
  previousPayslips?: Array<{
    grossCents: number
    cotisationsCents: number
    abattementCents: number
    imposableCents: number
    impotsCents: number
    netCents: number
    aVerserCents: number
  }>
}

export function buildBulletinData(input: BuildInput): BulletinData {
  const { result: r, employee: emp, company, year, month } = input
  const startDate = `01/${String(month).padStart(2, '0')}/${year}`
  const endDate = `${lastDayOfMonth(year, month)}/${String(month).padStart(2, '0')}/${year}`

  // Build lines like LPG
  const lines: BulletinLine[] = []

  // Salaire
  lines.push({ code: 'SAL', libelle: 'SALAIRES', qte: 1, salBase: r.grossBaseCents, total: r.grossBaseCents, retenue: null })
  if (r.overtimeCents > 0) {
    lines.push({ code: 'HS', libelle: 'Heures supplementaires +40%', qte: input.overtimeHours, salBase: null, total: r.overtimeCents, retenue: null })
  }
  if (r.thirteenthMonthCents > 0) {
    lines.push({ code: '13M', libelle: '13eme mois', qte: 1, salBase: r.thirteenthMonthCents, total: r.thirteenthMonthCents, retenue: null })
  }

  // Brut Total
  lines.push({ code: '', libelle: 'Brut Total', qte: null, salBase: null, total: r.grossTotalCents, retenue: null, isBold: true })

  // Cotisations
  const cappedGross = r.cappedGrossCents
  lines.push({ code: '', libelle: 'Caisse de maladie (Soins) 2,80%', qte: null, salBase: cappedGross, total: null, retenue: r.ssEmployeeMaladieCents - Math.round(cappedGross * 0.25 / 100) })
  lines.push({ code: '', libelle: 'Caisse de maladie (Especes) 0,25%', qte: null, salBase: cappedGross, total: null, retenue: Math.round(cappedGross * 0.25 / 100) })
  lines.push({ code: '', libelle: 'Caisse de pension 8,50%', qte: null, salBase: cappedGross, total: null, retenue: r.ssEmployeePensionCents })
  lines.push({ code: '', libelle: 'Assurance dependance 1,40%', qte: null, salBase: r.dependanceBaseCents, total: null, retenue: r.ssEmployeeDependanceCents })

  // Abattements
  if (r.abatementFDCents > 0) {
    lines.push({ code: 'FD', libelle: 'Frais de deplacement', qte: null, salBase: r.abatementFDCents, total: null, retenue: null })
  }
  if (r.abatementACCents > 0) {
    lines.push({ code: 'AC', libelle: 'Abattement conjoint', qte: null, salBase: AC_MENSUEL_CENTS, total: null, retenue: null })
  }
  lines.push({ code: 'FFO', libelle: 'Forfait Frais d\'obtention', qte: null, salBase: FFO_MENSUEL_CENTS, total: null, retenue: null })
  lines.push({ code: 'FDS', libelle: 'Forfait Depenses speciales', qte: null, salBase: FDS_MENSUEL_CENTS, total: null, retenue: null })

  // Cheques-repas
  if (r.mealVouchersEmployerCents > 0) {
    const voucherValue = r.mealVouchersEmployerCents > 0 && input.mealVouchersCount > 0 ? Math.round(r.mealVouchersEmployerCents / input.mealVouchersCount) : 1080
    lines.push({ code: 'CHQR', libelle: 'Cheque Repas patron', qte: input.mealVouchersCount, salBase: voucherValue, total: r.mealVouchersEmployerCents, retenue: null })
  }

  // Imposable + Impots
  const rtsLabel = emp.rtsPersonalRate ? `Impots  Taux : ${emp.rtsPersonalRate}%` : 'Impots (bareme)'
  lines.push({ code: '', libelle: 'Imposable', qte: null, salBase: null, total: r.taxableIncomeCents, retenue: null })
  lines.push({ code: '', libelle: rtsLabel, qte: null, salBase: null, total: null, retenue: r.rtsCents })

  // Net
  lines.push({ code: '', libelle: 'Net', qte: null, salBase: null, total: r.netCents, retenue: null, isBold: true })

  // Credits
  lines.push({ code: 'CIS', libelle: 'Credit d\'impot salarie', qte: 1, salBase: r.creditCISCents, total: r.creditCISCents, retenue: null })
  if (r.creditCICCents > 0) {
    lines.push({ code: 'CIC', libelle: 'Credit d\'impot conjoint', qte: 1, salBase: r.creditCICCents, total: r.creditCICCents, retenue: null })
  }
  lines.push({ code: 'CICO2', libelle: 'Credit d\'impot CO2', qte: 1, salBase: r.creditCICO2Cents, total: r.creditCICO2Cents, retenue: null })

  // Patronal detail
  const mutRate = CLASSES_MUTUALITE['I'] ?? 0.69
  const cappedForPatronal = Math.min(r.grossTotalCents, PLAFOND_COTISABLE_CENTS)
  const patMaladieSoins = Math.round(cappedForPatronal * COTISATIONS_PATRONALES.maladie_soins / 100)
  const patMaladieEspeces = Math.round(cappedForPatronal * COTISATIONS_PATRONALES.maladie_especes / 100)
  const patPension = Math.round(cappedForPatronal * COTISATIONS_PATRONALES.pension / 100)
  const patAccident = Math.round(cappedForPatronal * COTISATIONS_PATRONALES.accident_travail / 100)
  const patMutualite = Math.round(cappedForPatronal * mutRate / 100)
  const patSante = Math.round(cappedForPatronal * COTISATIONS_PATRONALES.sante_travail / 100)
  const patTotal = patMaladieSoins + patMaladieEspeces + patPension + patAccident + patMutualite + patSante

  return {
    company: {
      name: company.name,
      address: '',
      codeSociete: '',
      matSociete: '',
    },
    employee: {
      codeSal: '',
      civilite: '',
      firstName: emp.firstName,
      lastName: emp.lastName,
      address: '',
      noSecu: emp.matriculeCcss ?? '',
      noCarte: '',
      profession: emp.position ?? '',
      taxClass: emp.taxClass,
      taxType: 'M',
      entryDate: '',
      seniorityDate: '',
      exitDate: '',
      iban: '',
      banque: '',
      indice: '968.04',
    },
    period: {
      year, month,
      startDate, endDate,
      joursImp: 26,
      joursCot: lastDayOfMonth(year, month),
      hresMois: Math.round((emp.weeklyHours ?? 40) * 52 / 12),
    },
    lines,
    summary: {
      brutCents: r.grossTotalCents,
      cotisationsCents: r.ssEmployeeTotalCents,
      abattementCents: r.totalAbatementsCents,
      imposableCents: r.taxableIncomeCents,
      impotsCents: r.rtsCents,
      netCents: r.netCents,
      aVerserCents: r.aVerserCents,
    },
    cumuls: buildCumuls(r, input.previousPayslips),
    conges: {
      repConges: 0,
      congeSup: 0,
      congeLegaux: 26,
      congePris: 0,
      soldeCongeDays: 26,
      soldeCongeHrs: 0,
      valeurJour: 0,
    },
    patronal: {
      maladieSoinsCents: patMaladieSoins,
      maladieEspecesCents: patMaladieEspeces,
      pensionCents: patPension,
      accidentCents: patAccident,
      mutualiteCents: patMutualite,
      santeCents: patSante,
      totalCents: patTotal,
    },
    totalCostCents: r.grossTotalCents + patTotal + r.mealVouchersEmployerCents,
    language: 'fr',
  }
}

/** Cumuls = mois courant + somme des mois precedents */
function buildCumuls(
  current: PayrollResult,
  previous?: BuildInput['previousPayslips'],
): BulletinData['cumuls'] {
  const prev = previous ?? []
  const sumPrev = (key: keyof NonNullable<BuildInput['previousPayslips']>[0]) =>
    prev.reduce((s, p) => s + p[key], 0)

  return {
    brutCents: current.grossTotalCents + sumPrev('grossCents'),
    cotisationsCents: current.ssEmployeeTotalCents + sumPrev('cotisationsCents'),
    abattementCents: current.totalAbatementsCents + sumPrev('abattementCents'),
    imposableCents: current.taxableIncomeCents + sumPrev('imposableCents'),
    impotsCents: current.rtsCents + sumPrev('impotsCents'),
    netCents: current.netCents + sumPrev('netCents'),
    aVerserCents: current.aVerserCents + sumPrev('aVerserCents'),
  }
}
